import React, { useRef } from 'react';
import cn from 'clsx';
import { Price } from 'fronton-react';
import { Divider, Link, PriceView, Section, SectionSubTitle, Typography } from 'common/components';
import { useIntersection } from 'common/hooks';
import { Product, VariantAxisProduct } from 'modules/collections';
import { createElementKey, enhanceImageUrl } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { ProductCollapse } from '../product-collapse';
import {
  collectionComposition,
  collectionProduct,
  collectionProductImage,
  collectionProductImageWrapper,
  collectionProductInfo,
  collectionProductInfoDivider,
  collectionProductList,
  collectionProductName,
  collectionProductPriceBlock,
  collectionProductPriceWrapper,
  collectionProductTwoRowsPriceWrapper,
} from './product-composition-styles';

type ProductCompositionProps = {
  productDivision: string;
  collectionInfo: VariantAxisProduct;
  elementBlock: string;
  onShowMoreClick?: (buttonName: string, elementName: string) => void;
};

const getPriceWithDiscount = (price: number, discount?: number) => {
  if (!discount) {
    return price;
  }

  return Math.round(price - (price * discount) / 100);
}

export const ProductComposition: React.FC<ProductCompositionProps> = ({
  productDivision,
  collectionInfo,
  elementBlock,
  onShowMoreClick,
}) => {
  const productRef = useRef<HTMLAnchorElement[]>([]);

  useIntersection(productRef, el => {
    const position = el.getAttribute('data-position');

    if (!position) {
      return;
    }

    const product = collectionInfo.products[Number(position)];
    analyticsUtils.sendEvent('GA:impressions', {
      division: productDivision,
      elementBlock: `${elementBlock} / Состав комплекта`,
      elementType: 'product',
      productFamilyId: undefined,
      productId: product.productId,
      komplektyId: collectionInfo.productId,
      productName: product.displayedName,
      productPosition: (+position + 1).toString(),
      productPrevPrice: null,
      productPrice: product.pricePerSingle,
      subdivision: '',
    });
  });

  const handleLinkClick = (product: Product, position: number) => () => {
    analyticsUtils.sendEvent('GA:clickProduct', {
      division: productDivision,
      elementBlock: `${elementBlock} / Состав комплекта`,
      elementType: 'product',
      productFamilyId: undefined,
      productId: product.productId,
      komplektyId: collectionInfo.productId,
      productName: product.displayedName,
      productPosition: position + 1,
      productPrevPrice: null,
      productPrice: product.pricePerSingle,
      subdivision: '',
    });
  };

  return (
    <Section className={collectionComposition}>
      <SectionSubTitle adaptiveMargin margin="m">
        Состав комплекта
      </SectionSubTitle>
      <ProductCollapse elementName="Состав комплекта" onShowMoreClick={onShowMoreClick}>
        <div className={collectionProductList}>
          {collectionInfo.products.map((product, index) => (
            <Link
              className={collectionProduct}
              data-position={index}
              href={product.productUrl}
              onClick={handleLinkClick(product, index)}
              key={createElementKey('product', index)}
              refCb={(el: HTMLAnchorElement) => {
                if (el) productRef.current[index] = el;
              }}
              title={product.displayedName}>
              <div className={collectionProductImageWrapper}>
                <img
                  alt={product.displayedName}
                  className={collectionProductImage}
                  height={70}
                  src={enhanceImageUrl(product.mediaPhoto)}
                  title={product.displayedName}
                  width={70}
                />
              </div>
              <div className={collectionProductInfo}>
                <div className={collectionProductName}>
                  <Typography variant="caption" color="text-minor">
                    Арт. {product.productId}
                  </Typography>
                  <Typography variant="m" size="body_short">
                    {product.displayedName}
                  </Typography>
                </div>
                <div className={collectionProductPriceBlock}>
                  <Typography variant="m" size="body_short">
                    {product.quantity} шт.
                  </Typography>
                  <div
                    className={cn(collectionProductPriceWrapper, {
                      [collectionProductTwoRowsPriceWrapper]: product.quantity > 1,
                    })}>
                      <PriceView
                        discount={collectionInfo.price.discount}
                        price={getPriceWithDiscount(product.pricePerSingle * product.quantity, collectionInfo.price.discount)}
                        prevPrice={product.pricePerSingle * product.quantity}
                        size="l"
                        showDiscountPlate={product.quantity === 1}
                      />
                    {product.quantity > 1 && (
                      <Price
                        color="text-secondary"
                        value={product.pricePerSingle}
                        units=" ₽/шт."
                        size="xs"
                      />
                    )}
                  </div>
                </div>

                <Divider className={collectionProductInfoDivider} />
              </div>
            </Link>
          ))}
        </div>
      </ProductCollapse>
    </Section>
  );
};
